<script setup lang="ts">
import { Page, Skeleton, Section, Button } from "@/components/Ui";
import { ReviewItem } from "@/components/Reviews";
import {
  BaseAffiliate
} from "@/components/Sections";
import { useRoute, useRouter } from "vue-router";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useTextDirection } from '@vueuse/core'

const route = useRoute();
const router = useRouter();
const localePath = useLocalePath();
const {t} = useI18n();
const dir = useTextDirection();
const params = {
  fields: ["title", "slug", "review_items"],
  "filters[slug][$eq]": route.params.slug
};

const paramsPlatform = {
  fields: ["title", "slug", "review_items"],
  "filters[slug][$eq]": route.params.slug
};
const itemsParams = {
  "sort[date]": "desc",
  fields: ["date", "review_platform"],
  "filters[review_platform][slug]": route.params.slug
};
const [{ pending: pendingPlatform, data: platformData }, {
  data: itemsData
}] = await Promise.all([
  useFetch("/api/review-platforms", { params: paramsPlatform }),
  useFetch("/api/review-items", { params: itemsParams })
]);

const platform = computed(() => {
  return platformData.value?.data[0]?.attributes;
});
const title = computed<string>(() => {
  return platform.value?.title === 'BHW'
    ? t("page.reviewsForum.seo.title", { d: platform.value?.title })
    : t("page.reviewsPlatform.seo.title", { d: platform.value?.title })
});
const description = computed<string>(() => {
  return platform.value?.title === 'BHW'
    ? t("page.reviewsForum.seo.description", { d: platform.value?.title })
    : t("page.reviewsPlatform.seo.description", { d: platform.value?.title })
});

const h1 = computed<string>(() => {
  return platform.value?.title === 'BHW'
    ? t("reviews.forumH1", { d: platform.value?.title })
    : t("reviews.platformH1", { d: platform.value?.title })
})

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
});
</script>

<template>
  <Page :class="$style.root">
    <Section :class="$style.head">
      <div :class="$style['head-info']">
        <Skeleton :pending="pendingPlatform" :options="{ width: 84, height: 84 }">
          <template #loader>
            <circle cx="42" cy="42" r="42" />
          </template>
          <nuxt-img
            v-if="platform?.slug"
            :src="`/img/review-platform/${platform?.slug}-circle.svg`"
            :class="$style['head-icon']"
            loading="lazy"
          />
        </Skeleton>
        <Skeleton :pending="pendingPlatform" :options="{ width: 320, height: 40 }">
          <template #loader>
            <rect x="0" y="0" rx="16" ry="16" width="320" height="40" />
          </template>
          <h1 :class="[$style.title, $style[`title-${$i18n.locale}`]]" v-html="h1" />
        </Skeleton>
      </div>
    </Section>
    <Section :class="$style.reviews">
      <Button
        :title="$t('btn.seeAllReviews')"
        icon="chevron-left"
        :icon-pos="dir === 'ltr' ? 'left' : 'right'"
        size="custom"
        color="custom"
        :class="$style.back"
        @click="router.push(localePath({path: '/reviews' }))"
      >
      </Button>
      <div :class="$style['reviews-list']">
        <ReviewItem
          v-for="(item, index) in itemsData?.data"
          :key="index"
          :itemId="item.id"
        />
      </div>
    </Section>
    <BaseAffiliate />
  </Page>
</template>

<style lang="scss" module>
.root {
  @apply mt-[70px] md:mt-[90px];
  & h1 {
    @apply text-center text-neutral-800 font-black max-w-[540px];
  }
}

.head {
  @apply pt-8 pb-[80px];

  &-info {
    @apply flex flex-col items-center gap-6;
  }

  &-icon {
    @apply w-[84px] h-[84px] rounded-full;
  }
}
.title {
  @apply leading-none;
  @apply text-[40px];
  &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
  &-be,
  &-it,
  &-pt,
  &-tr,
  &-ua,
  &-es,
  &-fr,
  &-ja,
  &-bg,
  &-ru,
  &-zh,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw{
    @apply text-[40px];
  }
  &-de {
    @apply text-[28px] xxs:text-[36px] 414:text-[40px];
  }
  &-zh br {
    @apply md:hidden;
  }
  &-zh,
  &-ja {
    @apply leading-[1.2];
  }
}
.reviews {
  @apply gap-5 mb-[60px];

  &-list {
    @apply flex flex-col gap-5 w-full;
  }
}

.back {
  @apply w-fit text-[20px] mr-auto font-medium md:py-[18px];

  &:hover svg {
    @apply -translate-x-1;
  }

  & svg {
    @apply w-6 h-6 min-w-[24px] transition-all;
  }
}
</style>